import { useMemo, useState } from 'react';
import { mensajeDe, type Categoria, type ParejaAdmin } from '../../services/api';
import { useClienteTorneo } from '../../services/clienteTorneo';
import { Aviso, Boton, Tarjeta, Titulo } from '../../components/ui';
import { nombrePareja } from '../../components/Grupos';
import { textoDeLado, VistaCuadro } from '../../components/Cuadro';
import { resolverCuadro, sembrarDesdeGrupos, sembrarEliminacion, sortearSiembra, type Lugar } from '../../nucleo/cuadro';
import { resolverCuadroGuardado } from '../../nucleo/vistaCuadro';
import { marcadorDeFila } from '../../nucleo/vistaGrupos';
import { FormResultado } from './Grupos';

// El cuadro desde el lado del organizador: armarlo (sale de los grupos, o de
// un sorteo en solo eliminación) y cargar los resultados de cada cruce.

export function SeccionCuadro({ categoria, recargar }: { categoria: Categoria<ParejaAdmin>; recargar: () => Promise<void> }) {
  if (!categoria.cuadro) return <ArmarCuadro categoria={categoria} recargar={recargar} />;
  return <CuadroArmado categoria={categoria} recargar={recargar} />;
}

function datosDe(categoria: Categoria<ParejaAdmin>) {
  return {
    parejas: new Map(categoria.parejas.map((p) => [p.id, p])),
    letras: new Map(categoria.grupos.map((g) => [g.id, g.letra])),
    bajas: new Set(categoria.parejas.filter((p) => p.estado === 'baja').map((p) => p.id)),
  };
}

// ── Armar ─────────────────────────────────────────────────────────────────

function ArmarCuadro({ categoria, recargar }: { categoria: Categoria<ParejaAdmin>; recargar: () => Promise<void> }) {
  const api = useClienteTorneo();
  const [abierto, setAbierto] = useState(false);
  const [siembra, setSiembra] = useState<number[] | null>(null);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  const conGrupos = categoria.formato === 'grupos';
  const pasan = categoria.pasan_por_grupo ?? 1;
  const inscriptas = categoria.parejas.filter((p) => p.estado === 'inscripta');
  const { parejas, letras } = datosDe(categoria);

  // Lo que se va a guardar, para mirarlo antes de confirmar.
  const lugares: Lugar[] | null = conGrupos
    ? sembrarDesdeGrupos(categoria.grupos.map((g) => g.id), pasan)
    : siembra && sembrarEliminacion(siembra);
  const vistaPrevia = lugares && resolverCuadro(lugares, [], new Map());

  if (conGrupos && categoria.grupos.length === 0) return null; // primero los grupos
  if (conGrupos && categoria.grupos.length * pasan < 2) {
    return <p className="mt-3 text-sm text-(--color-apagado)">Con un solo grupo y un clasificado no hay cuadro: gana el primero del grupo.</p>;
  }
  if (!conGrupos && inscriptas.length < 2) {
    return <p className="mt-3 text-sm text-(--color-apagado)">Con 2 parejas inscriptas o más se puede armar el cuadro.</p>;
  }

  if (!abierto) {
    return (
      <div className="mt-3">
        <Boton
          chico
          onClick={() => {
            setAbierto(true);
            if (!conGrupos) setSiembra(sortearSiembra(inscriptas));
          }}
        >
          Armar cuadro
        </Boton>
      </div>
    );
  }

  const confirmar = async () => {
    setEnviando(true);
    setError('');
    try {
      await api.armarCuadro(categoria.id, conGrupos ? undefined : (siembra ?? undefined));
      await recargar();
    } catch (e) {
      setError(mensajeDe(e));
    } finally {
      setEnviando(false);
    }
  };

  const primera = vistaPrevia?.rondas[0] ?? [];
  const porRonda = vistaPrevia?.rondas.map((r) => r.length) ?? [];
  const directas = primera.filter((c) => c.pasaDirecto).length;

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-xl border border-(--color-borde) p-3">
      <p className="text-sm font-semibold">
        {conGrupos
          ? `Pasan ${pasan} por grupo: ${categoria.grupos.length * pasan} parejas al cuadro. Así quedan los cruces:`
          : `${inscriptas.length} parejas. Así quedó el sorteo:`}
      </p>
      <ol className="flex flex-col gap-1.5 text-sm">
        {primera.map((c) => {
          const a = textoDeLado(c.a, parejas, letras, porRonda);
          const b = textoDeLado(c.b, parejas, letras, porRonda);
          return (
            <li key={c.pos} className="rounded-lg border border-(--color-borde) px-2.5 py-1.5">
              {c.pasaDirecto ? (
                <>
                  <b>{'libre' in c.a ? b.texto : a.texto}</b> <span className="text-(--color-apagado)">pasa directo</span>
                </>
              ) : (
                <>
                  <b>{a.texto}</b> <span className="text-(--color-apagado)">contra</span> <b>{b.texto}</b>
                </>
              )}
            </li>
          );
        })}
      </ol>
      <p className="text-xs text-(--color-apagado)">
        {conGrupos
          ? 'Dos del mismo grupo no se cruzan en la primera ronda y quedan en mitades distintas del cuadro. Se puede armar ahora aunque los grupos no hayan terminado: cada lugar se completa solo.'
          : 'Las cabezas de serie van separadas; el resto se sortea.'}
        {directas > 0 && ` Como no llegan a llenar el cuadro, ${directas === 1 ? 'la mejor sembrada pasa' : `las ${directas} mejores sembradas pasan`} directo.`}
      </p>
      {error && <Aviso tono="error">{error}</Aviso>}
      <div className="flex flex-wrap gap-2">
        <Boton chico onClick={confirmar} deshabilitado={enviando}>
          {enviando ? 'Guardando...' : 'Confirmar cuadro'}
        </Boton>
        {!conGrupos && (
          <Boton chico tipo="secundario" onClick={() => setSiembra(sortearSiembra(inscriptas))}>
            Sortear de nuevo
          </Boton>
        )}
        <Boton chico tipo="secundario" onClick={() => setAbierto(false)}>
          Cancelar
        </Boton>
      </div>
    </div>
  );
}

// ── Armado: la llave y sus resultados ─────────────────────────────────────

function CuadroArmado({ categoria, recargar }: { categoria: Categoria<ParejaAdmin>; recargar: () => Promise<void> }) {
  const api = useClienteTorneo();
  const [editando, setEditando] = useState<number | null>(null);
  const [error, setError] = useState('');
  const guardado = categoria.cuadro!;

  const { parejas, letras, cuadro } = useMemo(() => {
    const d = datosDe(categoria);
    return { ...d, cuadro: resolverCuadroGuardado(categoria.cuadro!, categoria.grupos, d.bajas, categoria.pasan_por_grupo ?? 1) };
  }, [categoria]);

  const hayResultados = guardado.partidos.some((p) => p.estado === 'jugado');

  const deshacer = async () => {
    setError('');
    try {
      await api.deshacerCuadro(categoria.id);
      await recargar();
    } catch (e) {
      setError(mensajeDe(e));
    }
  };

  return (
    <div className="mt-4 flex flex-col gap-3">
      {error && <Aviso tono="error">{error}</Aviso>}
      <VistaCuadro
        cuadro={cuadro}
        parejas={parejas}
        letras={letras}
        paraOrganizador
        extraPartido={(c) => {
          const fila = guardado.partidos.find((p) => p.id === c.id);
          if (!fila) return null;
          const jugado = fila.estado === 'jugado';
          if (!jugado && !c.sePuedeCargar) return null;
          if (editando === c.id && 'pareja' in c.a && 'pareja' in c.b) {
            return (
              <FormResultado
                a={nombrePareja(parejas.get(c.a.pareja))}
                b={nombrePareja(parejas.get(c.b.pareja))}
                inicial={jugado ? marcadorDeFila(fila) : null}
                alCancelar={() => setEditando(null)}
                alGuardar={async (m) => {
                  await api.cargarResultado(c.id, m);
                  setEditando(null);
                  await recargar();
                }}
                alAnular={
                  jugado
                    ? async () => {
                        await api.anularResultado(c.id);
                        setEditando(null);
                        await recargar();
                      }
                    : undefined
                }
              />
            );
          }
          return (
            <button onClick={() => setEditando(c.id)} className="mt-1 text-xs font-semibold text-(--color-acento)">
              {jugado ? 'Corregir' : 'Cargar resultado'}
            </button>
          );
        }}
      />
      {!hayResultados && (
        <Tarjeta>
          <Titulo>¿Te equivocaste?</Titulo>
          <p className="mb-3 text-sm text-(--color-apagado)">Mientras no haya resultados en el cuadro, se puede deshacer y volver a armar.</p>
          <Boton chico tipo="peligro" onClick={deshacer}>
            Deshacer cuadro
          </Boton>
        </Tarjeta>
      )}
    </div>
  );
}
